import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { productsService } from '../../../../services/products/products.service';
import { variantsCombosService } from '../../../../services/variants-combos/variants-combos.service';
import type { PosLimitItemsSectionProps, ResetConfirmState, StatFilter } from './types';

const SEARCH_DEBOUNCE_MS = 200;

const hasOutOfStockOption = (groups: PosLimitItemsSectionProps['rootGroupsByProductId'][number]): boolean =>
  groups.some((group) =>
    group.items.some((item) => item.inStock === false || hasOutOfStockOption(item.childGroups)),
  );

const countOutOfStockOptions = (groups: PosLimitItemsSectionProps['rootGroupsByProductId'][number]): number =>
  groups.reduce(
    (total, group) =>
      total +
      group.items.reduce(
        (groupTotal, item) => groupTotal + Number(item.inStock === false) + countOutOfStockOptions(item.childGroups),
        0,
      ),
    0,
  );

// Nested option items are limitable here, so they are searchable at any depth.
const optionItemMatches = (
  groups: PosLimitItemsSectionProps['rootGroupsByProductId'][number],
  needle: string,
): boolean =>
  groups.some((group) =>
    group.items.some((item) => item.name.toLowerCase().includes(needle) || optionItemMatches(item.childGroups, needle)),
  );

const collectOptionItemIds = (
  groups: PosLimitItemsSectionProps['rootGroupsByProductId'][number],
  acc: number[] = [],
): number[] => {
  for (const group of groups) {
    for (const item of group.items) {
      acc.push(item.id);
      collectOptionItemIds(item.childGroups, acc);
    }
  }
  return acc;
};

// Keeps only branches that lead to a match; a matching item keeps its own subtree intact.
const pruneGroupsBySearch = (
  groups: PosLimitItemsSectionProps['rootGroupsByProductId'][number],
  needle: string,
): PosLimitItemsSectionProps['rootGroupsByProductId'][number] =>
  groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => (item.name.toLowerCase().includes(needle)
          ? item
          : { ...item, childGroups: pruneGroupsBySearch(item.childGroups, needle) }))
        .filter((item) => item.name.toLowerCase().includes(needle) || item.childGroups.length > 0),
    }))
    .filter((group) => group.items.length > 0);

export const usePosLimitItemsController = ({
  posProducts,
  rootGroupsByProductId,
  tenantId,
  employeeSession,
  isManager,
  onLimitSaveError,
}: Pick<PosLimitItemsSectionProps, 'posProducts' | 'rootGroupsByProductId' | 'tenantId' | 'employeeSession' | 'isManager' | 'onLimitSaveError'>) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [statFilter, setStatFilter] = useState<StatFilter>('all');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(() => new Set());
  const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(() => new Set());
  const [selectedOptionItemIds, setSelectedOptionItemIds] = useState<Set<number>>(() => new Set());
  const [numPadProductId, setNumPadProductId] = useState<number | null>(null);
  const [bulkSelection, setBulkSelection] = useState<number[] | null>(null);
  const [isSavingLimit, setIsSavingLimit] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [resetConfirm, setResetConfirm] = useState<ResetConfirmState | null>(null);
  const stockWriteInFlightRef = useRef(false);

  useEffect(() => {
    if (isManager) {
      return;
    }
    setExpandedProductIds((previous) => {
      const next = new Set(previous);
      for (const product of posProducts) {
        if (hasOutOfStockOption(rootGroupsByProductId[product.id] ?? [])) {
          next.add(product.id);
        }
      }
      return next;
    });
  }, [isManager, posProducts, rootGroupsByProductId]);

  // A product can match purely on a nested option name, so reveal the rows that explain the match.
  useEffect(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) {
      return;
    }
    setExpandedProductIds((previous) => {
      let next: Set<number> | null = null;
      for (const product of posProducts) {
        if (previous.has(product.id) || product.name.toLowerCase().includes(needle)) {
          continue;
        }
        if (optionItemMatches(rootGroupsByProductId[product.id] ?? [], needle)) {
          next = next ?? new Set(previous);
          next.add(product.id);
        }
      }
      return next ?? previous;
    });
  }, [debouncedSearch, posProducts, rootGroupsByProductId]);

  const visibleBaseProducts = useMemo(
    () =>
      isManager
        ? posProducts
        : posProducts.filter(
            (product) => product.limit !== null || hasOutOfStockOption(rootGroupsByProductId[product.id] ?? []),
          ),
    [isManager, posProducts, rootGroupsByProductId],
  );

  const filteredProducts = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    return visibleBaseProducts.filter((product) => {
      if (statFilter === 'limited' && !(product.limit !== null && product.limit > 0)) {
        return false;
      }
      if (statFilter === 'out' && product.limit !== 0 && !hasOutOfStockOption(rootGroupsByProductId[product.id] ?? [])) {
        return false;
      }
      if (needle
        && !product.name.toLowerCase().includes(needle)
        && !optionItemMatches(rootGroupsByProductId[product.id] ?? [], needle)) {
        return false;
      }
      return true;
    });
  }, [visibleBaseProducts, debouncedSearch, rootGroupsByProductId, statFilter]);

  const visibleSelectedProductIds = useMemo(
    () => filteredProducts.filter((product) => selectedProductIds.has(product.id)).map((product) => product.id),
    [filteredProducts, selectedProductIds],
  );

  const visibleGroupsByProductId = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const map: PosLimitItemsSectionProps['rootGroupsByProductId'] = {};
    for (const product of filteredProducts) {
      const groups = rootGroupsByProductId[product.id] ?? [];
      map[product.id] = needle && !product.name.toLowerCase().includes(needle)
        ? pruneGroupsBySearch(groups, needle)
        : groups;
    }
    return map;
  }, [filteredProducts, rootGroupsByProductId, debouncedSearch]);

  const filteredOptionItemIds = useMemo(
    () => filteredProducts.flatMap((product) => collectOptionItemIds(visibleGroupsByProductId[product.id] ?? [])),
    [filteredProducts, visibleGroupsByProductId],
  );

  const visibleClearableProductIds = useMemo(
    () => filteredProducts.filter((product) => product.limit !== null).map((product) => product.id),
    [filteredProducts],
  );

  const visibleSelectedClearableProductIds = useMemo(
    () =>
      filteredProducts
        .filter((product) => selectedProductIds.has(product.id) && product.limit !== null)
        .map((product) => product.id),
    [filteredProducts, selectedProductIds],
  );

  const totalCount = visibleBaseProducts.length;
  const outOfStockCount = useMemo(
    () =>
      visibleBaseProducts.reduce(
        (total, product) =>
          total + Number(product.limit === 0) + countOutOfStockOptions(rootGroupsByProductId[product.id] ?? []),
        0,
      ),
    [rootGroupsByProductId, visibleBaseProducts],
  );
  const limitedCount = useMemo(
    () => visibleBaseProducts.filter((product) => product.limit !== null && product.limit > 0).length,
    [visibleBaseProducts],
  );

  const persistLimits = useCallback(
    async (productIds: number[], value: number | null): Promise<boolean> => {
      if (!employeeSession || !isManager) {
        const message = 'רק מנהל יכול לעדכן הגבלת מוצר';
        setLimitError(message);
        onLimitSaveError?.(message);
        return false;
      }
      if (productIds.length === 0) {
        return false;
      }
      setIsSavingLimit(true);
      setLimitError(null);
      try {
        await productsService.updateLimits(productIds, value, employeeSession);
        return true;
      } catch (saveError: unknown) {
        const message = saveError instanceof Error ? saveError.message : 'Failed to save limit';
        setLimitError(message);
        onLimitSaveError?.(message);
        return false;
      } finally {
        setIsSavingLimit(false);
      }
    },
    [employeeSession, isManager, onLimitSaveError],
  );

  const persistItemStock = useCallback(
    async (itemIds: number[], inStock: boolean): Promise<void> => {
      if (!employeeSession || !isManager || itemIds.length === 0 || stockWriteInFlightRef.current) {
        return;
      }
      stockWriteInFlightRef.current = true;
      setLimitError(null);
      try {
        await variantsCombosService.setItemsInStock(tenantId, itemIds, inStock, employeeSession);
        setSelectedOptionItemIds((previous) => {
          const next = new Set(previous);
          itemIds.forEach((id) => next.delete(id));
          return next;
        });
      } catch (saveError: unknown) {
        const message = saveError instanceof Error ? saveError.message : 'Failed to update item stock';
        setLimitError(message);
        onLimitSaveError?.(message);
      } finally {
        stockWriteInFlightRef.current = false;
      }
    },
    [employeeSession, isManager, onLimitSaveError, tenantId],
  );

  const handleClearLimit = useCallback(
    (productId: number) => {
      void persistLimits([productId], null);
    },
    [persistLimits],
  );

  const executeClearLimits = (confirmedReset: ResetConfirmState) => {
    const targetIdSet = new Set(confirmedReset.productIds);
    const scope = posProducts.filter((product) => targetIdSet.has(product.id));
    const targetIds = scope.filter((product) => product.limit !== null).map((product) => product.id);
    void persistLimits(targetIds, null).then((ok) => {
      if (ok && confirmedReset.kind === 'selected') {
        setSelectedProductIds((previous) => {
          const next = new Set(previous);
          confirmedReset.productIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    });
  };

  const handleClearLimits = () => {
    if (selectedProductIds.size > 0) {
      if (visibleSelectedClearableProductIds.length === 0) {
        return;
      }
      setResetConfirm({ kind: 'selected', productIds: visibleSelectedClearableProductIds });
      return;
    }
    if (visibleClearableProductIds.length === 0) {
      return;
    }
    setResetConfirm({ kind: 'all', productIds: visibleClearableProductIds });
  };

  const handleConfirmReset = () => {
    if (!resetConfirm) {
      return;
    }
    const confirmedReset = resetConfirm;
    setResetConfirm(null);
    executeClearLimits(confirmedReset);
  };

  const hasSameLimitForAll = (productIds: number[], value: number | null): boolean => {
    if (productIds.length === 0) {
      return false;
    }
    return productIds.every((id) => (posProducts.find((product) => product.id === id)?.limit ?? null) === value);
  };

  const handleToggleSelected = useCallback((productId: number, checked: boolean) => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  }, []);

  const handleToggleProductExpanded = useCallback((productId: number) => {
    setExpandedProductIds((previous) => {
      const next = new Set(previous);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const handleToggleOptionItemSelected = useCallback((itemId: number, checked: boolean) => {
    setSelectedOptionItemIds((previous) => {
      const next = new Set(previous);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  const handleSetOptionItemStock = useCallback((itemIds: number[], inStock: boolean) => {
    void persistItemStock(itemIds, inStock);
  }, [persistItemStock]);

  const handleToggleSelectAllFiltered = () => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      const allSelected = filteredProducts.every((product) => next.has(product.id));
      filteredProducts.forEach((product) => {
        if (allSelected) {
          next.delete(product.id);
        } else {
          next.add(product.id);
        }
      });
      return next;
    });
  };

  const handleToggleSelectAllOptionItems = () => {
    setSelectedOptionItemIds((previous) => {
      const next = new Set(previous);
      const allSelected = filteredOptionItemIds.every((itemId) => next.has(itemId));
      filteredOptionItemIds.forEach((itemId) => {
        if (allSelected) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }
      });
      return next;
    });
    // Selected rows must be visible for the stock actions to make sense.
    setExpandedProductIds((previous) => {
      const next = new Set(previous);
      filteredProducts.forEach((product) => {
        if ((rootGroupsByProductId[product.id] ?? []).length > 0) {
          next.add(product.id);
        }
      });
      return next;
    });
  };

  const handleOpenNumPad = useCallback((productId: number) => {
    setLimitError(null);
    setBulkSelection(null);
    setNumPadProductId(productId);
  }, []);

  const handleOpenBulkNumPad = () => {
    if (visibleSelectedProductIds.length === 0) {
      return;
    }
    setLimitError(null);
    setNumPadProductId(null);
    setBulkSelection(visibleSelectedProductIds);
  };

  const handleClearSelection = () => {
    setSelectedProductIds((previous) => {
      const next = new Set(previous);
      visibleSelectedProductIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleCloseNumPad = () => {
    setNumPadProductId(null);
    setBulkSelection(null);
  };

  const handleConfirmNumPad = async (value: number | null) => {
    if (bulkSelection !== null) {
      if (hasSameLimitForAll(bulkSelection, value)) {
        setBulkSelection(null);
        return;
      }
      const ok = await persistLimits(bulkSelection, value);
      if (ok) {
        setBulkSelection(null);
        setSelectedProductIds(new Set());
      }
      return;
    }
    if (numPadProductId !== null) {
      const currentLimit = posProducts.find((product) => product.id === numPadProductId)?.limit ?? null;
      if (currentLimit === value) {
        setNumPadProductId(null);
        return;
      }
      const ok = await persistLimits([numPadProductId], value);
      if (ok) {
        setNumPadProductId(null);
      }
    }
  };

  const editingProduct = useMemo(
    () => (numPadProductId === null ? null : posProducts.find((p) => p.id === numPadProductId) ?? null),
    [numPadProductId, posProducts],
  );

  const selectedCount = visibleSelectedProductIds.length;
  const hasSelection = selectedCount > 0;
  const selectedClearableCount = visibleSelectedClearableProductIds.length;
  const hasGlobalSelection = selectedProductIds.size > 0;
  const hasClearAction = hasGlobalSelection
    ? selectedClearableCount > 0
    : visibleClearableProductIds.length > 0;
  const clearButtonLabel = hasSelection ? `אפס (${selectedClearableCount})` : 'אפס הכל';

  const allFilteredSelected =
    filteredProducts.length > 0 && filteredProducts.every((product) => selectedProductIds.has(product.id));
  const someFilteredSelected =
    !allFilteredSelected && filteredProducts.some((product) => selectedProductIds.has(product.id));
  const hasOptionItemsInView = filteredOptionItemIds.length > 0;
  const allOptionItemsSelected =
    hasOptionItemsInView && filteredOptionItemIds.every((itemId) => selectedOptionItemIds.has(itemId));
  const someOptionItemsSelected =
    !allOptionItemsSelected && filteredOptionItemIds.some((itemId) => selectedOptionItemIds.has(itemId));
  const hasSelectionActions = isManager && hasSelection;

  const isBulkMode = bulkSelection !== null;
  const numPadOpen = editingProduct !== null || isBulkMode;
  const numPadTitle = isBulkMode ? 'הגדר מגבלה לפריטים שנבחרו' : 'הגדר מגבלת פריט';
  const numPadSubject = isBulkMode
    ? `${bulkSelection?.length ?? 0} פריטים נבחרו`
    : (editingProduct?.name ?? '');
  const numPadInitial = isBulkMode ? null : (editingProduct?.limit ?? null);
  const resetConfirmMessage =
    resetConfirm?.kind === 'selected'
      ? `האם אתה בטוח שברצונך לאפס הגבלות עבור ${resetConfirm.productIds.length} פריטים?`
      : 'האם אתה בטוח שברצונך לאפס את כל ההגבלות';

  return {
    search,
    statFilter,
    selectedProductIds,
    expandedProductIds,
    selectedOptionItemIds,
    isSavingLimit,
    limitError,
    resetConfirm,
    filteredProducts,
    visibleGroupsByProductId,
    totalCount,
    outOfStockCount,
    limitedCount,
    hasClearAction,
    clearButtonLabel,
    allFilteredSelected,
    someFilteredSelected,
    hasOptionItemsInView,
    allOptionItemsSelected,
    someOptionItemsSelected,
    hasSelectionActions,
    numPadOpen,
    numPadTitle,
    numPadSubject,
    numPadInitial,
    resetConfirmMessage,
    setSearch,
    setStatFilter,
    setResetConfirm,
    handleToggleSelectAllFiltered,
    handleToggleSelectAllOptionItems,
    handleOpenBulkNumPad,
    handleClearSelection,
    handleClearLimits,
    handleToggleSelected,
    handleToggleProductExpanded,
    handleToggleOptionItemSelected,
    handleSetOptionItemStock,
    handleOpenNumPad,
    handleClearLimit,
    handleCloseNumPad,
    handleConfirmNumPad,
    handleConfirmReset,
  };
};
